import { useState, useEffect, useRef } from 'react'
import { useDeepCallback } from '@retailer-platform/shared-common'

type MockApolloError = {
  message: string
}

type HookResult<T> = {
  data: T
  loading: boolean
  error?: MockApolloError
  called: boolean
  refetch: () => void
}

/**
 * This function exists for shimming queries that are yet to be done.
 * It will take an expected data object and will return it as one would
 * expect when using an apollo useQuery hook.
 *
 * For instance:
 * ```ts
 * const useMockAccountsQuery = genMockQueryHook({
 *  accounts: [
 *    { id: 10, name: 'Jimmy' }
 *    { id: 20, name: 'Bob' }
 *  ]
 * })
 *
 * const Component = () => {
 *    const { data, loading, called } = useMockAccountsQuery()
 * }
 * ```
 *
 * Note that the functionality only works for successful calls right now, but feel free to expand on it!
 *
 * @param expectedData
 */
export function genMockQueryHook<V, T>(
  expectedData: T,
  mockOptions: { lazy: true }
): (options: { variables?: V }) => [() => Promise<T>, HookResult<T>]
export function genMockQueryHook<V, T>(
  expectedData: T,
  mockOptions: { lazy: false }
): (options: { variables?: V; skip?: boolean }) => HookResult<T>
export function genMockQueryHook<V, T>(expectedData: T, mockOptions: { lazy: boolean }) {
  return (options: { variables?: V; skip?: boolean } = {}) => {
    const timeoutHandler = useRef(null)
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<T>(null)
    const [called, setCalled] = useState(false)
    const fetch = useDeepCallback(() => {
      console.info('Triggered mock query with:', options)

      setLoading(true)
      setCalled(true)

      clearTimeout(timeoutHandler.current)

      return new Promise(resolve => {
        timeoutHandler.current = setTimeout(() => {
          setData(expectedData)
          setLoading(false)
          resolve(expectedData)
        }, 2000)
      })
    }, [options])

    useEffect(() => {
      if (mockOptions.lazy || options.skip) return

      fetch()
    }, [fetch, options.skip])

    useEffect(() => () => clearTimeout(timeoutHandler.current), [])

    const refetch = () => {
      /* NOOP */
    }

    const hookResult: HookResult<T> = { data, loading, called, refetch } as const

    return mockOptions.lazy ? ([fetch, hookResult] as const) : hookResult
  }
}
